import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html } from 'lit';
import { workTaskList } from '../../db/work-task-resolvers.js';
import type { WorkTaskFeedbackLink, WorkTaskListResult } from '../../db/work-task-types.js';
import { TestFeedbackPage } from './TestFeedbackPage.js';
import { WorkTasksPanel, type WorkTaskRequest } from './WorkTasksPanel.js';

export const FeedbackWorkspace = stateful(() => {
  const [activeTab, setActiveTab] = useState<'feedback' | 'tasks'>('feedback');
  const [request, setRequest] = useState<WorkTaskRequest | null>(null);
  const [sequence] = useState({ value: 0 });
  // A failure loading Tasks never prevents the existing Feedback panel from working.
  const accessQuery = useQuery(() => workTaskList()(), []);
  let canManage = false;
  try {
    const value = accessQuery.value;
    const result = (typeof value === 'string' ? JSON.parse(value) : value) as WorkTaskListResult | undefined;
    canManage = result?.access?.canManage === true;
  } catch {
    // Task actions stay hidden until the server confirms access.
  }
  const openTask = (mode: 'create' | 'link', feedback: WorkTaskFeedbackLink) => {
    setRequest({ mode, feedback, token: ++sequence.value });
    setActiveTab('tasks');
  };
  const switchTab = (tab: 'feedback' | 'tasks') => {
    setActiveTab(tab);
    queueMicrotask(() => document.getElementById(`zw-workspace-tab-${tab}`)?.focus());
  };
  return html`
    <style>
      .zw-feedback-workspace-tabs {
        max-width: 1420px;
        margin: 28px auto 0;
        padding: 0 4px;
        border-bottom: 1px solid #dde3ec;
        display: flex;
        gap: 26px;
      }
      .zw-feedback-workspace-tabs button {
        border: 0;
        border-bottom: 3px solid transparent;
        background: transparent;
        color: #718096;
        padding: 14px 3px 15px;
        margin-bottom: -1px;
        font: inherit;
        font-size: 14px;
        font-weight: 650;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 9px;
      }
      .zw-feedback-workspace-tabs button[aria-selected='true'] {
        color: #c8001c;
        border-bottom-color: #d6001c;
      }
      .zw-feedback-workspace-tabs button:hover {
        color: #b40019;
      }
      .zw-feedback-workspace-tabs button:focus-visible {
        outline: 2px solid #d6001c;
        outline-offset: 4px;
        border-radius: 3px;
      }
      .zw-feedback-workspace-tabs svg {
        width: 17px;
        height: 17px;
      }
      @media (max-width: 1480px) {
        .zw-feedback-workspace-tabs {
          margin-left: 30px;
          margin-right: 30px;
        }
      }
      @media (max-width: 640px) {
        .zw-feedback-workspace-tabs {
          margin: 16px 16px 0;
        }
      }
    </style>
    <div class="zw-feedback-workspace-tabs" role="tablist" aria-label="Feedback und Aufgaben">
      ${(['feedback', 'tasks'] as const).map(
        tab =>
          html` <button
            type="button"
            role="tab"
            id=${`zw-workspace-tab-${tab}`}
            aria-controls=${`zw-workspace-panel-${tab}`}
            aria-selected=${activeTab === tab ? 'true' : 'false'}
            tabindex=${activeTab === tab ? '0' : '-1'}
            @click=${() => switchTab(tab)}
            @keydown=${(event: KeyboardEvent) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              switchTab(
                event.key === 'Home'
                  ? 'feedback'
                  : event.key === 'End'
                    ? 'tasks'
                    : activeTab === 'feedback'
                      ? 'tasks'
                      : 'feedback',
              );
            }}
          >
            ${tab === 'feedback'
              ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
                  <path d="M5 4h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6 4V6a2 2 0 0 1 2-2Z" />
                  <path d="M7 9h10M7 13h6" />
                </svg>`
              : html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">
                  <rect x="4" y="3" width="16" height="18" rx="2" />
                  <path d="m7 9 2 2 3-4M14 9h3M7 16h10" />
                </svg>`}
            ${tab === 'feedback' ? 'Feedback' : 'Tasks'}
          </button>`,
      )}
    </div>
    <!-- Keep both panels mounted so switching tabs preserves drafts and existing Feedback state. -->
    <div
      id="zw-workspace-panel-feedback"
      role="tabpanel"
      aria-labelledby="zw-workspace-tab-feedback"
      ?hidden=${activeTab !== 'feedback'}
    >
      ${TestFeedbackPage({
        onCreateTask: canManage ? source => openTask('create', source) : undefined,
        onLinkTask: canManage ? source => openTask('link', source) : undefined,
      })}
    </div>
    <div
      id="zw-workspace-panel-tasks"
      role="tabpanel"
      aria-labelledby="zw-workspace-tab-tasks"
      ?hidden=${activeTab !== 'tasks'}
    >
      ${WorkTasksPanel({ request, onRequestHandled: () => setRequest(null) })}
    </div>
  `;
});
